import { create } from 'zustand';
import { mroDashboardService } from '../services/mroDashboardService';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import type {
  MroDashboardSummaryDto,
  RecentMrsDto,
  TopRequestorDto,
  FastMovingItemDto,
  TopRequestedItemDto,
  RequestAgingDto,
  MovementTrendDto,
  InventoryMovementDto,
} from '../types';

interface DashboardState {
  // Data
  summary: MroDashboardSummaryDto | null;
  recentMrs: RecentMrsDto[];
  recentMovements: InventoryMovementDto[];
  topRequestors: TopRequestorDto[];
  fastMovingItems: FastMovingItemDto[];
  topRequestedItems: TopRequestedItemDto[];
  requestAging: RequestAgingDto[];
  movementTrends: MovementTrendDto[];

  // Loading states
  isLoading: boolean;
  isSummaryLoading: boolean;
  error: string | null;

  // Auto-refresh
  refreshInterval: number; // in milliseconds
  lastRefresh: string | null;
  autoRefreshEnabled: boolean;

  // Actions
  fetchAll: () => Promise<void>;
  fetchSummary: () => Promise<void>;
  fetchRecentMrs: () => Promise<void>;
  fetchRecentMovements: () => Promise<void>;
  fetchTopRequestors: () => Promise<void>;
  fetchFastMovingItems: () => Promise<void>;
  fetchTopRequestedItems: () => Promise<void>;
  fetchRequestAging: () => Promise<void>;
  fetchMovementTrends: () => Promise<void>;
  setAutoRefresh: (enabled: boolean) => void;
  setRefreshInterval: (interval: number) => void;
  clearError: () => void;
}

export const useDashboardStore = create<DashboardState>()((set) => ({
  // Data
  summary: null,
  recentMrs: [],
  recentMovements: [],
  topRequestors: [],
  fastMovingItems: [],
  topRequestedItems: [],
  requestAging: [],
  movementTrends: [],

  // Loading states
  isLoading: false,
  isSummaryLoading: false,
  error: null,

  // Auto-refresh (default: 30 seconds)
  refreshInterval: 30000,
  lastRefresh: null,
  autoRefreshEnabled: true,

  fetchAll: async () => {
    set({ isLoading: true, error: null });
    try {
      const [
        summary,
        recentMrs,
        recentMovements,
        topRequestors,
        fastMovingItems,
        topRequestedItems,
        requestAging,
        movementTrends,
      ] = await Promise.all([
        mroDashboardService.getSummary(),
        mroDashboardService.getRecentMrs(15),
        mroDashboardService.getRecentMovements(15),
        mroDashboardService.getTopRequestors({ limit: 10 }),
        mroDashboardService.getFastMovingItems({ limit: 10 }),
        mroDashboardService.getTopRequestedItems({ limit: 10 }),
        mroDashboardService.getRequestAging(),
        mroDashboardService.getMovementTrends(),
      ]);

      set({
        summary,
        recentMrs,
        recentMovements,
        topRequestors,
        fastMovingItems,
        topRequestedItems,
        requestAging,
        movementTrends,
        isLoading: false,
        lastRefresh: new Date().toISOString(),
      });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to fetch dashboard data');
      set({ error: message, isLoading: false });
    }
  },

  fetchSummary: async () => {
    set({ isSummaryLoading: true });
    try {
      const summary = await mroDashboardService.getSummary();
      set({ summary, isSummaryLoading: false });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to fetch summary');
      set({ error: message, isSummaryLoading: false });
    }
  },

  fetchRecentMrs: async () => {
    try {
      const recentMrs = await mroDashboardService.getRecentMrs(15);
      set({ recentMrs });
    } catch (error) {
      console.error('Failed to fetch recent MRS:', error);
    }
  },

  fetchRecentMovements: async () => {
    try {
      const recentMovements = await mroDashboardService.getRecentMovements(15);
      set({ recentMovements });
    } catch (error) {
      console.error('Failed to fetch recent movements:', error);
    }
  },

  fetchTopRequestors: async () => {
    try {
      const topRequestors = await mroDashboardService.getTopRequestors({ limit: 10 });
      set({ topRequestors });
    } catch (error) {
      console.error('Failed to fetch top requestors:', error);
    }
  },

  fetchFastMovingItems: async () => {
    try {
      const fastMovingItems = await mroDashboardService.getFastMovingItems({ limit: 10 });
      set({ fastMovingItems });
    } catch (error) {
      console.error('Failed to fetch fast moving items:', error);
    }
  },

  fetchTopRequestedItems: async () => {
    try {
      const topRequestedItems = await mroDashboardService.getTopRequestedItems({ limit: 10 });
      set({ topRequestedItems });
    } catch (error) {
      console.error('Failed to fetch top requested items:', error);
    }
  },

  fetchRequestAging: async () => {
    try {
      const requestAging = await mroDashboardService.getRequestAging();
      set({ requestAging });
    } catch (error) {
      console.error('Failed to fetch request aging:', error);
    }
  },

  fetchMovementTrends: async () => {
    try {
      const movementTrends = await mroDashboardService.getMovementTrends();
      set({ movementTrends });
    } catch (error) {
      console.error('Failed to fetch movement trends:', error);
    }
  },

  setAutoRefresh: (enabled: boolean) => {
    set({ autoRefreshEnabled: enabled });
  },

  setRefreshInterval: (interval: number) => {
    set({ refreshInterval: interval });
  },

  clearError: () => {
    set({ error: null });
  },
}));
